import { saveAs } from 'file-saver';
import { createTimeInstance } from '@utils/time';
// The date-window vocabulary is shared with the score-spec list, so it lives in its own leaf module
// rather than here — this file drags in file-saver and the doc-type tables, which that list has no
// use for. Re-exported below so every existing importer of @utils/documentVault keeps working.
import { DATE_RANGE_PRESETS, resolveDateRange } from '@utils/dateRange';
// Imported from the constants leaf, not from @utils/doc: that module reaches into
// @services/api, which would drag the whole service layer into these pure helpers.
import { unassignedDocGroupName } from '@constants/documents';

export { DATE_RANGE_PRESETS, DATE_RANGE_PRESET_LABELS, resolveDateRange } from '@utils/dateRange';

export const DOCUMENT_DOWNLOAD_KINDS = {
  original: 'original',
  clean: 'clean',
};

export const DOCUMENT_STATUSES = {
  ready: 'ready',
  processing: 'processing',
  failed: 'failed',
};

export const DOCUMENT_STATUS_LABELS = {
  [DOCUMENT_STATUSES.ready]: 'Ready',
  [DOCUMENT_STATUSES.processing]: 'Processing',
  [DOCUMENT_STATUSES.failed]: 'Failed',
};

export const FILE_FORMATS = {
  pdf: 'pdf',
  other: 'other',
};

export const FILE_FORMAT_LABELS = {
  [FILE_FORMATS.pdf]: 'PDF',
  [FILE_FORMATS.other]: 'Non-PDF',
};

export const SORT_KEYS = {
  filename: 'filename',
  docType: 'docType',
  company: 'company',
  uploaded: 'uploaded',
};

export const UNASSIGNED_TAG = unassignedDocGroupName;

export const VAULT_VIEW_MODES = {
  table: 'table',
  tree: 'tree',
};

export const VAULT_DENSITIES = {
  comfortable: 'comfortable',
  compact: 'compact',
};

export const VAULT_FACETS = {
  tickers: 'tickers',
  docTypes: 'docTypes',
  tags: 'tags',
  statuses: 'statuses',
  fileFormats: 'fileFormats',
};

export const DEFAULT_PAGE_SIZE = 50;

export const initialFilterState = {
  search: '',
  tickers: [],
  docTypes: [],
  tags: [],
  statuses: [],
  fileFormats: [],
  cleanEligibleOnly: false,
  dateRange: { preset: DATE_RANGE_PRESETS.all, from: null, to: null },
  sort: { key: SORT_KEYS.uploaded, direction: 'desc' },
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  viewMode: VAULT_VIEW_MODES.table,
  density: VAULT_DENSITIES.comfortable,
};

/*
  Same grouping the rest of the app shows in its folder tree: ticker first, then tag, then the
  unassigned bucket. Mirrors parseDocGroup, which lives in @utils/doc and cannot be imported
  here without dragging in the service layer.
*/
export function getDocumentGroupLabel(doc) {
  const { security_symbol, security_name, doc_tag } = doc || {};

  if (security_symbol) {
    return security_name ? `${security_symbol} - ${security_name}` : security_symbol;
  }
  if (doc_tag?.name) return doc_tag.name;
  return UNASSIGNED_TAG;
}

export function groupDocumentsByLabel(docs) {
  const groups = {};

  (docs || []).forEach((doc) => {
    const label = getDocumentGroupLabel(doc);
    if (!groups[label]) groups[label] = [];
    groups[label].push(doc);
  });

  // Unassigned sorts first, everything else alphabetically -- the same order DocumentOrganizer
  // uses so the two screens read alike.
  const labels = Object.keys(groups).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  const ordered = labels.includes(UNASSIGNED_TAG)
    ? [UNASSIGNED_TAG, ...labels.filter((l) => l !== UNASSIGNED_TAG)]
    : labels;

  return ordered.map((label) => ({ label, docs: groups[label] }));
}

/*
  Doc filenames in this codebase carry stray quotes -- DocumentTree strips them the same way
  when sorting.
*/
function normalizeFilename(filename) {
  return String(filename ?? '')
    .split('"')
    .join('')
    .trim();
}

/*
  Single source of truth for clean-copy eligibility.

  `content_type` first with the filename extension as fallback mirrors the backend's own PDF
  test, so the client can never mark a doc eligible that the server then passes through
  unmodified, or grey out a PDF the server would have cleaned.

  Doc-list rows spell this `content_type`; upload-status rows spell it `doc_content_type`. The
  Vault holds both shapes at once, so both are normalized here rather than at each call site.
*/
export function isPdfDocument(doc) {
  if (!doc) return false;

  const contentType = doc.content_type ?? doc.doc_content_type;
  if (contentType) {
    return contentType === 'application/pdf';
  }

  return /\.pdf$/i.test(normalizeFilename(doc.filename));
}

export function parseContentDispositionFilename(header) {
  if (!header) return null;

  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (!match) return null;

  const filename = normalizeFilename(decodeURIComponentSafe(match[1]));
  return filename === '' ? null : filename;
}

function decodeURIComponentSafe(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
}

export function saveBlobAsFile(blob, filename) {
  saveAs(blob, filename);
}

export function buildZipFilename(count) {
  return `documents-${count}.zip`;
}

export const DEFAULT_NAMING_TEMPLATE = '{filename}';
export const CLEAN_COPY_SUFFIX = '_clean';

export const NAMING_TEMPLATE_TOKENS = ['{filename}', '{ticker}', '{doc_type}', '{date}'];

// Characters no common filesystem accepts in a name.
const ILLEGAL_FILENAME_CHARACTERS = /[\\/:*?"<>|]/g;

function sanitizeFilenamePart(value) {
  return String(value ?? '')
    .replace(ILLEGAL_FILENAME_CHARACTERS, '')
    .trim();
}

function splitExtension(filename) {
  const name = normalizeFilename(filename) || 'document';
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex <= 0) return { base: name, extension: '' };
  return { base: name.slice(0, dotIndex), extension: name.slice(dotIndex) };
}

/*
  Renames a download from a user-supplied pattern. Tokens that have no value on a given document
  collapse away rather than leaving "undefined" in the filename, and the extension is always
  carried over from the original so the file still opens.
*/
export function applyNamingTemplate(doc, pattern, { clean = false } = {}) {
  const { base, extension } = splitExtension(doc?.filename);

  const values = {
    '{filename}': base,
    '{ticker}': getDocumentTicker(doc) || '',
    '{doc_type}': doc?.doc_type || '',
    '{date}': doc?.created_at ? createTimeInstance(doc.created_at).format('YYYY-MM-DD') : '',
  };

  const template = pattern && pattern.trim() ? pattern : DEFAULT_NAMING_TEMPLATE;

  let result = template;
  Object.keys(values).forEach((token) => {
    result = result.split(token).join(sanitizeFilenamePart(values[token]));
  });

  result = sanitizeFilenamePart(result)
    // An absent token leaves separators stranded; collapse them so names stay readable.
    .replace(/[_\-\s]{2,}/g, '_')
    .replace(/^[_\-\s]+|[_\-\s]+$/g, '');

  if (!result) result = base;
  if (clean) result += CLEAN_COPY_SUFFIX;

  return `${result}${extension}`;
}

/*
  Two documents can legitimately carry the same filename. Inside a ZIP the second would silently
  overwrite the first, so collisions get a numeric suffix before the extension.
*/
export function dedupeZipEntryNames(filename, usedNames) {
  const name = normalizeFilename(filename) || 'document';
  if (!usedNames.includes(name)) return name;

  const dotIndex = name.lastIndexOf('.');
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const extension = dotIndex > 0 ? name.slice(dotIndex) : '';

  let counter = 2;
  let candidate = `${base} (${counter})${extension}`;
  while (usedNames.includes(candidate)) {
    counter += 1;
    candidate = `${base} (${counter})${extension}`;
  }
  return candidate;
}

/* ******************************************************
  FACET VALUES
****************************************************** */

export function getDocumentTicker(doc) {
  return doc?.security_symbol || null;
}

export function getDocumentTag(doc) {
  if (doc?.doc_tag?.name) return doc.doc_tag.name;
  // Matches parseDocGroup: a doc with neither a ticker nor a tag lands in "Unassigned".
  if (!doc?.security_symbol) return UNASSIGNED_TAG;
  return null;
}

export function getDocumentFileFormat(doc) {
  return isPdfDocument(doc) ? FILE_FORMATS.pdf : FILE_FORMATS.other;
}

/*
  There is no per-doc processing status on the document record. The upload-status feed
  (v1/genai_user_crawl_jobs_get, mirrored into Redux) tracks only recent jobs, so any doc it
  doesn't mention has finished processing long ago and counts as ready.
*/
export function buildDocumentStatusLookup(uploadStatusData) {
  const lookup = {};
  if (!uploadStatusData) return lookup;

  const assign = (rows, status) => {
    (rows || []).forEach((row) => {
      const id = row?.doc_id ?? row?.id;
      if (id !== undefined && id !== null) {
        lookup[id] = status;
      }
    });
  };

  assign(uploadStatusData.completed, DOCUMENT_STATUSES.ready);
  assign(uploadStatusData.pending, DOCUMENT_STATUSES.processing);
  assign(uploadStatusData.errored, DOCUMENT_STATUSES.failed);

  return lookup;
}

/*
  Counts documents the upload-status feed reports as finished but that the loaded list doesn't
  contain yet -- the precise "there is newer data to load" signal.

  Deliberately not the Redux newlyCompletedDocsCount: that counter is a running delta which
  counts every completed job the first time the store is populated, so a freshly opened screen
  would claim new documents that are already on it.
*/
export function countNewlyProcessedDocs(uploadStatusData, docs) {
  const completed = uploadStatusData?.completed || [];
  if (completed.length === 0) return 0;

  const loadedIds = new Set((docs || []).map((doc) => doc.id));

  return completed.filter((row) => {
    const id = row?.doc_id ?? row?.id;
    return id !== undefined && id !== null && !loadedIds.has(id);
  }).length;
}

export function getDocumentStatus(doc, statusLookup) {
  return statusLookup?.[doc?.id] || DOCUMENT_STATUSES.ready;
}

/* ******************************************************
  FILTERING
****************************************************** */

function matchesSearch(doc, search) {
  if (!search) return true;
  return normalizeFilename(doc?.filename).toLowerCase().includes(search.trim().toLowerCase());
}

function matchesFacet(value, selected) {
  // An empty facet is "no opinion", not "match nothing".
  if (!selected || selected.length === 0) return true;
  return selected.includes(value);
}

function matchesDateRange(doc, resolvedRange) {
  const { from, to } = resolvedRange;
  if (!from && !to) return true;
  if (!doc?.created_at) return false;

  const createdAt = createTimeInstance(doc.created_at);
  if (from && createdAt.isBefore(from)) return false;
  if (to && createdAt.isAfter(to)) return false;
  return true;
}

/*
  AND across facets, OR within a facet.
*/
export function filterDocuments(docs, filters, statusLookup) {
  if (!docs) return [];
  if (!filters) return docs;

  const resolvedRange = resolveDateRange(filters.dateRange);

  return docs.filter((doc) => {
    if (!matchesSearch(doc, filters.search)) return false;
    if (!matchesFacet(getDocumentTicker(doc), filters.tickers)) return false;
    if (!matchesFacet(doc?.doc_type, filters.docTypes)) return false;
    if (!matchesFacet(getDocumentTag(doc), filters.tags)) return false;
    if (!matchesFacet(getDocumentStatus(doc, statusLookup), filters.statuses)) return false;
    if (!matchesFacet(getDocumentFileFormat(doc), filters.fileFormats)) return false;
    if (filters.cleanEligibleOnly && !isPdfDocument(doc)) return false;
    if (!matchesDateRange(doc, resolvedRange)) return false;
    return true;
  });
}

export function collectFacetOptions(docs, statusLookup) {
  const counts = {
    tickers: {},
    docTypes: {},
    tags: {},
    statuses: {},
    fileFormats: {},
  };

  (docs || []).forEach((doc) => {
    const ticker = getDocumentTicker(doc);
    if (ticker) counts.tickers[ticker] = (counts.tickers[ticker] || 0) + 1;

    if (doc?.doc_type) counts.docTypes[doc.doc_type] = (counts.docTypes[doc.doc_type] || 0) + 1;

    const tag = getDocumentTag(doc);
    if (tag) counts.tags[tag] = (counts.tags[tag] || 0) + 1;

    const status = getDocumentStatus(doc, statusLookup);
    counts.statuses[status] = (counts.statuses[status] || 0) + 1;

    const format = getDocumentFileFormat(doc);
    counts.fileFormats[format] = (counts.fileFormats[format] || 0) + 1;
  });

  const toOptions = (map, labelFor) =>
    Object.keys(map)
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .map((value) => ({ value, label: labelFor ? labelFor(value) : value, count: map[value] }));

  return {
    tickers: toOptions(counts.tickers),
    docTypes: toOptions(counts.docTypes),
    tags: toOptions(counts.tags),
    statuses: toOptions(counts.statuses, (value) => DOCUMENT_STATUS_LABELS[value] || value),
    fileFormats: toOptions(counts.fileFormats, (value) => FILE_FORMAT_LABELS[value] || value),
  };
}

/* ******************************************************
  SORTING
****************************************************** */

function compareStrings(a, b) {
  const left = String(a ?? '').toLowerCase();
  const right = String(b ?? '').toLowerCase();
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function sortValue(doc, key) {
  if (key === SORT_KEYS.filename) return normalizeFilename(doc?.filename).toLowerCase();
  if (key === SORT_KEYS.docType) return doc?.doc_type || '';
  if (key === SORT_KEYS.company) return getDocumentTicker(doc) || '';
  return null;
}

export function sortDocuments(docs, sort) {
  if (!docs) return [];

  const key = sort?.key || SORT_KEYS.uploaded;
  const direction = sort?.direction === 'asc' ? 1 : -1;
  const sorted = [...docs];

  sorted.sort((a, b) => {
    if (key === SORT_KEYS.uploaded) {
      const left = a?.created_at ? createTimeInstance(a.created_at).valueOf() : 0;
      const right = b?.created_at ? createTimeInstance(b.created_at).valueOf() : 0;
      return (left - right) * direction;
    }
    return compareStrings(sortValue(a, key), sortValue(b, key)) * direction;
  });

  return sorted;
}
